import { useLocale } from "@/i18n/LocaleProvider";
import { LocalizedText } from "@/i18n/LocalizedText";
import { useFormContext, Controller } from "react-hook-form";
import type { FieldError } from "react-hook-form";
import { Spinner } from "@/components/ui/spinner";
import { Label } from "@/components/ui/label";
import { Button } from "./ui/button";
import type { Brand } from "./AppSidebar";
import { useEffect, useState } from "react";
import CountriesSelect from "./ui/countries-dropdowm";
import { useLocation } from "react-router-dom";
import { Switch } from "./ui/switch";
import { BadgeCheck, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";

export function sanitizeString(input: string) {
  let str = input.trim();
  str = str.replace(/[<>{}`|]|[\p{Cf}\p{Cc}]/gu, " ");
  str = str.replace(/([^\p{L}\p{N}\s])\1+/gu, "$1");
  str = str.replace(/\s+/g, " ").trim();
  const lines = str.split("\n").slice(0, 4);
  str = lines.join("\n");
  return str;
}

interface Step2 {
  error: string;
  brand: Brand | null;
  additionalAction?: () => void;
  prefilled?: boolean;
  isScrollable?: boolean;
  company: any;
  loadingStep: boolean;
  registerBrand: (
    brandCountry: string,
    brandName: string,
    brandWebsite: string,
    currentFocus: string,
  ) => void;
}

export default function Step2({
  company,
  isScrollable = false,
  registerBrand,
  brand,
  additionalAction,
  error,
  loadingStep,
  prefilled = false,
}: Step2) {
  const { t } = useLocale();
  const {
    register,
    formState: { errors },
    getValues,
    clearErrors,
    setValue,
    control,
    watch,
  } = useFormContext();
  const location = useLocation();
  const [currentFocus, setCurrentFocus] = useState(false);

  useEffect(() => {
    const allParams = new URLSearchParams(location.search);
    const brandadd = allParams.get("brand");
    if (brandadd) {
      setValue("brandWebsite", brandadd);
    }
  }, []);

  useEffect(() => {
    if (brand && company) {
      const { url, name, currentFocus } = brand;
      if (prefilled) {
        setValue("brandName", name);
        setValue("brandWebsite", url);
        setCurrentFocus(true);
        setValue("currentFocus", currentFocus);
      }
      setValue("brandCountry", company.country);
    }
  }, [brand, setValue]);

  const onBrandRegister = () => {
    const { brandCountry, brandName, brandWebsite, currentFocus } = getValues();
    registerBrand(brandCountry, brandWebsite, brandName, currentFocus);
  };

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h2 className="text-xl font-semibold text-center mb-2 leading-7">
          <LocalizedText id="legacy.set.up.your.brand.1qsu3ig" />
        </h2>
      </div>
      <div
        className={`${isScrollable ? "flex flex-col gap-6 max-h-[42vh] overflow-y-auto" : "flex flex-col gap-6"}`}
      >
        <div>
          <Label className="text-sm font-medium mb-3" htmlFor="brandName">
            <LocalizedText id="legacy.brand.name.1udhmrd" />
          </Label>
          <input
            disabled={Boolean(prefilled && brand)}
            id="brandName"
            type="text"
            autoComplete="off"
            {...register("brandName")}
            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300"
          />
          {errors.brandName && (
            <p className="text-red-500 text-sm mt-2">
              {(errors.brandName as FieldError)?.message}
            </p>
          )}
        </div>

        <div>
          <Label className="text-sm font-medium mb-3" htmlFor="brandWebsite">
            <LocalizedText id="legacy.brand.website.193susl" />
          </Label>
          <input
            disabled={Boolean(prefilled && brand)}
            id="brandWebsite"
            type="text"
            autoComplete="off"
            {...register("brandWebsite")}
            className="w-full px-4 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-slate-300"
          />
          {errors.brandWebsite && (
            <p className="text-red-500 text-sm mt-2">
              {(errors.brandWebsite as FieldError)?.message}
            </p>
          )}
        </div>

        <div>
          <Controller
            name="brandCountry"
            control={control}
            rules={{ required: "Country is required" }}
            render={({ field, fieldState }) => {
              return (
                <>
                  <Label className="text-sm font-medium mb-3" htmlFor="country">
                    <LocalizedText id="legacy.primary.country.147f875" />
                  </Label>
                  <CountriesSelect
                    isFull={true}
                    promptCountry={field.value}
                    setPromptCountry={field.onChange}
                  ></CountriesSelect>
                  {fieldState.error && (
                    <p className="text-red-500 text-sm mt-2">
                      <LocalizedText id="legacy.country.is.required.1f4i2xq" />
                    </p>
                  )}
                  <div className="flex mt-6 gap-2 mb-2 items-center">
                    <Switch
                      id="currentFocus"
                      checked={currentFocus}
                      onCheckedChange={setCurrentFocus}
                      className={`cursor-pointer ${currentFocus ? "data-[state=checked]:bg-[#FF5E00]" : ""}`}
                    />
                    <Label
                      htmlFor="currentFocus"
                      className="cursor-pointer text-sm text-foreground"
                    >
                      <LocalizedText id="legacy.add.brand.context.optional.1hlc3gv" />
                    </Label>
                  </div>
                  {currentFocus && (
                    <div className="mt-4">
                      <div className="flex text-sm gap-2 items-center">
                        <LocalizedText id="legacy.current.focus.11htrc0" />
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Info
                              size={16}
                              className="cursor-pointer text-[#737373]"
                            ></Info>
                          </TooltipTrigger>
                          <TooltipContent className="max-w-[300px] text-sm">
                            <p>
                              {" "}
                              <LocalizedText id="legacy.mention.the.offer.service.or.feature.who.it.lfqsks" />
                            </p>
                          </TooltipContent>
                        </Tooltip>
                      </div>
                      <div className="relative mt-3">
                        <textarea
                          autoComplete="off"
                          placeholder={t(
                            "legacy.example.ai.automation.for.accounting.firms.target.cfos.3d6zyu",
                          )}
                          {...register("currentFocus")}
                          onBlur={(e) => {
                            const sanitized = sanitizeString(e.target.value);
                            setValue("currentFocus", sanitized, {
                              shouldValidate: true,
                            });
                          }}
                          className="h-[145px] border rounded-md w-full p-4 pb-7 resize-none"
                        />
                        <div className="absolute rounded-md flex w-[95%] justify-end bottom-[8px] left-[2px] bg-white p-1 text-sm text-[#737373] pointer-events-none">
                          {watch("currentFocus")?.length ?? 0}/240
                        </div>
                      </div>
                      {errors.currentFocus && (
                        <p className="text-red-500 text-sm mt-1">
                          {(errors.currentFocus as FieldError)?.message}
                        </p>
                      )}

                      <div className="bg-[#f5f5f5] p-2 mt-4 mb-2 rounded-md flex gap-1 items-center text-sm leading-4">
                        <BadgeCheck size={16} className="text-[#737373]" />
                        <LocalizedText id="legacy.you.can.update.this.later.in.settings.1x5zipd" />
                      </div>
                    </div>
                  )}
                </>
              );
            }}
          />
        </div>
      </div>
      {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      <div
        className={`flex ${additionalAction ? "justify-between" : "justify-center"}`}
      >
        {additionalAction ? (
          <Button
            type="button"
            className="cursor-pointer"
            onClick={() => {
              additionalAction();
              clearErrors();
            }}
            variant="outline"
          >
            <LocalizedText id="legacy.cancel.18x3nqb" />
          </Button>
        ) : null}
        <Button
          type="button"
          disabled={loadingStep}
          className={`flex cursor-pointer ${additionalAction ? "w-fit" : "w-fit py-[22px] px-[45px]"} hover:bg-[#ff8138] hover:text-white text-white bg-[#FF5E00]`}
          onClick={onBrandRegister}
        >
          {loadingStep ? (
            <div className=" mr-2 flex justify-center items-center">
              <Spinner variant="circle-filled" size={16}></Spinner>
            </div>
          ) : null}
          <LocalizedText id="legacy.continue.5iqttw" />
        </Button>
      </div>
    </div>
  );
}
